/**
 * LoadingOverlay Component
 *
 * A consistent, reusable loading overlay for long-running operations.
 * Can be used as a full-page overlay or within a container.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isLoading: boolean;
  /** Main title text */
  title?: string;
  /** Description text below the title */
  description?: string;
  /** Optional status text that can change during loading */
  statusText?: string;
  /** Whether to show as full-page overlay (default) or inline */
  variant?: 'fullPage' | 'inline' | 'card';
  /** Icon to show - 'sparkles' for AI operations, 'spinner' for general loading */
  icon?: 'sparkles' | 'spinner';
}

export function LoadingOverlay({
  isLoading,
  title = 'Loading...',
  description,
  statusText,
  variant = 'fullPage',
  icon = 'sparkles',
}: LoadingOverlayProps) {
  const [mounted, setMounted] = useState(false);

  // Ensure we're mounted before using portal
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!isLoading) return null;

  const content = (
    <div className="text-center space-y-6">
      {/* Animated icon container */}
      <div className="relative mx-auto w-24 h-24">
        {/* Outer rotating ring */}
        <div className="absolute inset-0 rounded-full border-4 border-mazo-200 animate-pulse"></div>
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-mazo-500 animate-spin"></div>
        {/* Inner icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="p-4 bg-gradient-to-br from-mazo-400 to-mazo-600 rounded-full shadow-lg">
            {icon === 'sparkles' ? (
              <img src="/logo-white.svg" alt="" className="h-8 w-8 animate-pulse" />
            ) : (
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            )}
          </div>
        </div>
      </div>

      {/* Text content */}
      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-gray-600 max-w-sm mx-auto">{description}</p>
        )}
      </div>

      {/* Status text with spinner */}
      {statusText && (
        <div className="flex items-center justify-center gap-2 text-sm text-mazo-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="animate-pulse">{statusText}</span>
        </div>
      )}

      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-2">
        <div
          className="w-2 h-2 rounded-full bg-mazo-500 animate-bounce"
          style={{ animationDelay: '0ms' }}
        ></div>
        <div
          className="w-2 h-2 rounded-full bg-mazo-400 animate-bounce"
          style={{ animationDelay: '150ms' }}
        ></div>
        <div
          className="w-2 h-2 rounded-full bg-mazo-300 animate-bounce"
          style={{ animationDelay: '300ms' }}
        ></div>
      </div>
    </div>
  );

  if (variant === 'fullPage') {
    const overlay = (
      <div className="fixed inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-center" style={{ zIndex: 99999 }}>
        {content}
      </div>
    );
    // Use portal to render at document body level, ensuring it's above everything
    if (mounted && typeof document !== 'undefined') {
      return createPortal(overlay, document.body);
    }
    return overlay;
  }

  if (variant === 'card') {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        {content}
      </div>
    );
  }

  // inline variant
  return (
    <div className="py-12 flex items-center justify-center">
      {content}
    </div>
  );
}

/**
 * Simple inline loader for buttons and small areas
 */
export function InlineLoader({ text = 'Loading...' }: { text?: string }) {
  return (
    <div className="flex items-center gap-2 text-gray-600">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
}

/**
 * Button loading state helper
 */
export function ButtonLoader({ text = 'Loading...' }: { text?: string }) {
  return (
    <>
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </>
  );
}

export default LoadingOverlay;
